import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getCoupons, updateCouponStatus } from '../services/couponService';
import { getRestaurantById } from '../services/restaurantService';

const PAGE_SIZE = 20;

const locationIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatCurrency(amount, currency = 'USD') {
  if (amount == null) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${currency || 'USD'} ${amount}`;
  }
}

function statusLabel(status) {
  if (!status) return 'Unknown';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function unwrapRestaurant(payload) {
  if (!payload) return null;
  if (payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data)) {
    return payload.data;
  }
  return payload;
}

export default function Coupons() {
  const { restaurantId: restaurantIdParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const restaurantId = restaurantIdParam || location.state?.restaurant?.id;
  const initialRestaurant = location.state?.restaurant || null;

  const [restaurant, setRestaurant] = useState(initialRestaurant);
  const [restaurantLoading, setRestaurantLoading] = useState(!initialRestaurant);
  const [restaurantError, setRestaurantError] = useState('');
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [actionTarget, setActionTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!restaurantId) {
      setRestaurantError('Partner venue is required.');
      setRestaurantLoading(false);
      return undefined;
    }

    let cancelled = false;

    async function loadRestaurant() {
      setRestaurantLoading(!initialRestaurant);
      setRestaurantError('');
      try {
        const response = await getRestaurantById(restaurantId);
        if (cancelled) return;
        setRestaurant(unwrapRestaurant(response));
      } catch (err) {
        if (cancelled) return;
        if (!initialRestaurant) {
          setRestaurantError(err.message || 'Failed to load partner venue details.');
        }
      } finally {
        if (!cancelled) setRestaurantLoading(false);
      }
    }

    loadRestaurant();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only refetch when restaurant id changes
  }, [restaurantId]);

  const fetchCoupons = useCallback(async () => {
    if (!restaurantId) {
      setError('Partner venue is required to load coupons.');
      setCoupons([]);
      setTotalPages(1);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await getCoupons({
        page,
        limit: PAGE_SIZE,
        status: statusFilter || undefined,
        search: debouncedSearch || undefined,
        sort: sortBy,
        restaurantId,
      });
      setCoupons(Array.isArray(response.data) ? response.data : []);
      setTotalPages(response.pagination?.totalPages || 1);
    } catch (err) {
      setError(err.message || 'Failed to fetch coupons.');
      setCoupons([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, debouncedSearch, sortBy, restaurantId]);

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  function openAction(coupon, status) {
    setActionError('');
    setActionTarget({ coupon, status });
  }

  function closeAction() {
    if (actionLoading) return;
    setActionTarget(null);
    setActionError('');
  }

  async function confirmAction() {
    if (!actionTarget) return;

    const { coupon, status } = actionTarget;
    setActionLoading(true);
    setActionError('');

    try {
      await updateCouponStatus(coupon.id, { status });
      setActionTarget(null);
      await fetchCoupons();
    } catch (err) {
      setActionError(err.message || `Failed to ${status === 'redeemed' ? 'redeem' : 'hide'} coupon.`);
    } finally {
      setActionLoading(false);
    }
  }

  const isRedeem = actionTarget?.status === 'redeemed';
  const actionTitle = isRedeem ? 'Redeem Coupon?' : 'Hide Coupon?';
  const actionMessage = isRedeem
    ? `Are you sure you want to redeem coupon ${actionTarget?.coupon?.couponCode}?`
    : `Are you sure you want to hide coupon ${actionTarget?.coupon?.couponCode}?`;
  const confirmLabel = isRedeem ? 'Redeem' : 'Hide Coupon';

  const restaurantStatus = restaurant?.status === 'inactive' ? 'Inactive' : 'Active';
  const restaurantLocation = restaurant?.location || restaurant?.address || 'No location';

  return (
    <div className="users-page coupons-page main-bg-top">
      <div className="coupons-top-bar">
        <button type="button" className="coupons-back-btn" onClick={() => navigate('/restaurants')}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5" />
            <path d="M12 19l-7-7 7-7" />
          </svg>
          Back
        </button>
      </div>

      {restaurantLoading && !restaurant ? (
        <div className="restaurant-detail-panel restaurant-detail-loading">Loading partner venue details...</div>
      ) : restaurantError && !restaurant ? (
        <div className="restaurant-detail-panel restaurant-detail-error">{restaurantError}</div>
      ) : restaurant ? (
        <section className="restaurant-detail-panel">
          <div className="restaurant-detail-image">
            {restaurant.imageUrl ? (
              <img src={restaurant.imageUrl} alt={restaurant.name || 'Partner venue'} />
            ) : (
              <div className="restaurant-card-image-placeholder">No image</div>
            )}
          </div>
          <div className="restaurant-detail-content">
            <div className="restaurant-detail-field">
              <span className="restaurant-detail-label">Name</span>
              <span className="restaurant-detail-value">{restaurant.name || '—'}</span>
            </div>
            <div className="restaurant-detail-field">
              <span className="restaurant-detail-label">Status</span>
              <span className="restaurant-detail-value">
                <span className={`status-badge status-${restaurantStatus.toLowerCase()}`}>
                  {restaurantStatus}
                </span>
              </span>
            </div>
            <div className="restaurant-detail-field">
              <span className="restaurant-detail-label">Location</span>
              <span className="restaurant-detail-value restaurant-detail-location-value">
                {locationIcon}
                {restaurantLocation}
              </span>
            </div>
            <div className="restaurant-detail-field">
              <span className="restaurant-detail-label">Description</span>
              <span className="restaurant-detail-value">
                {restaurant.description || 'No description provided.'}
              </span>
            </div>
          </div>
        </section>
      ) : null}

      <div className="users-header coupons-section-header">
        <h2>Coupons</h2>
        <div className="users-filters">
          <div className="users-search">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Search by coupon code..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="filter-select-wrap">
            <select
              className="filter-select"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="redeemed">Redeemed</option>
              <option value="expired">Expired</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div className="filter-select-wrap">
            <select
              className="filter-select restaurants-sort-select"
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value);
                setPage(1);
              }}
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
            </select>
          </div>
        </div>
      </div>

      <div className="users-table-wrap">
        <table className="users-table">
          <thead>
            <tr>
              <th>Coupon</th>
              <th>User</th>
              <th>Offer</th>
              <th>Petals</th>
              <th>Credit</th>
              <th>Issued</th>
              <th>Expires</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" className="users-empty">Loading coupons...</td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="9" className="users-empty">{error}</td>
              </tr>
            ) : coupons.length === 0 ? (
              <tr>
                <td colSpan="9" className="users-empty">No coupons found.</td>
              </tr>
            ) : (
              coupons.map((coupon) => {
                const status = statusLabel(coupon.status);
                const canAct = coupon.status === 'active';

                return (
                  <tr key={coupon.id}>
                    <td>
                      <div className="coupon-code-cell">
                        <span className="coupon-code">{coupon.couponCode || '—'}</span>
                        {coupon.redeemedAt && (
                          <span className="coupon-meta">Redeemed {formatDate(coupon.redeemedAt)}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="user-cell-info">
                        <span className="user-cell-name">{coupon.user?.name || '—'}</span>
                        <span className="user-cell-email">{coupon.user?.email || coupon.user?.phone || ''}</span>
                      </div>
                    </td>
                    <td>{coupon.offer?.title || '—'}</td>
                    <td>
                      <div className="petals-cell">
                        <img alt="Petals" className="menu-icon-img" src={`${process.env.PUBLIC_URL}/images/petals.svg`} />
                        {coupon.petalsSpent ?? 0}
                      </div>
                    </td>
                    <td>{formatCurrency(coupon.creditAmount, coupon.currency)}</td>
                    <td>{formatDate(coupon.issuedAt)}</td>
                    <td>{formatDate(coupon.expiresAt)}</td>
                    <td>
                      <span className={`status-badge status-${(coupon.status || 'unknown').toLowerCase()}`}>
                        {status}
                      </span>
                    </td>
                    <td>
                      {canAct ? (
                        <div className="actions-cell coupon-actions-cell">
                          <button
                            type="button"
                            className="approve-text-btn"
                            onClick={() => openAction(coupon, 'redeemed')}
                          >
                            Redeem
                          </button>
                          <button
                            type="button"
                            className="coupon-cancel-btn"
                            onClick={() => openAction(coupon, 'cancelled')}
                          >
                            Hide Coupon
                          </button>
                        </div>
                      ) : (
                        <span className="coupon-actions-na">—</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {actionTarget && (
        <div className="modal-overlay" onClick={closeAction}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">{actionTitle}</h3>
            <p className="modal-message">{actionMessage}</p>
            {actionError && <p className="coupon-action-error">{actionError}</p>}
            <div className="modal-actions">
              <button
                type="button"
                className="modal-btn modal-btn-cancel"
                onClick={closeAction}
                disabled={actionLoading}
              >
                Go Back
              </button>
              <button
                type="button"
                className={`modal-btn modal-btn-confirm ${isRedeem ? '' : 'modal-btn-danger'}`.trim()}
                onClick={confirmAction}
                disabled={actionLoading}
              >
                {actionLoading ? 'Please wait...' : confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}

      {!loading && !error && totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </button>
          <span className="pagination-info">Page {page} of {totalPages}</span>
          <button
            className="pagination-btn"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
